import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

/**
 * Custom hook for checking broker connection status (supports multiple brokers)
 * @param {Object} userAccount - User exchange account object
 * @param {number} refreshInterval - Refresh interval in milliseconds (default: 30000)
 * @returns {Object} { status, isChecking, lastChecked, refresh, supportsBrokerAPI }
 */
export const useIBGatewayConnection = (userAccount, refreshInterval = 30000) => {
  const [status, setStatus] = useState('Checking...');
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  // Check if this account supports broker API
  const supportsBrokerAPI = useCallback(() => {
    if (!userAccount) {
      console.log('supportsBrokerAPI: No userAccount');
      return false;
    }
    
    // Handle both nested (legacy) and flat (new trading app) structure
    const brokerSlug = userAccount.broker_slug || userAccount.exchange?.broker_slug;
    const exchangeName = userAccount.exchange_name || userAccount.exchange?.name;
    
    const hasSlug = brokerSlug && brokerSlug !== '';
    console.log('supportsBrokerAPI check:', {
      exchangeName: exchangeName,
      broker_slug: brokerSlug,
      hasSlug: hasSlug
    });
    
    // Check if account has broker_slug defined
    return hasSlug;
  }, [userAccount]);

  // Function to check broker connection
  const checkConnection = useCallback(async () => {
    console.log('checkConnection called with:', {
      hasUserAccount: !!userAccount,
      supportsBrokerAPI: supportsBrokerAPI(),
      userAccountId: userAccount?.id
    });

    if (!userAccount || !supportsBrokerAPI()) {
      console.log('checkConnection: No account or no broker API support');
      setStatus('N/A');
      return 'N/A';
    }

    console.log('Making broker connection API call...');
    setIsChecking(true);
    try {
      const response = await apiService.checkBrokerConnection({
        user_exchange_account_id: userAccount.id
      });
      
      console.log('Broker connection response:', response.data);
      const data = response.data;
      const newStatus = data.status === 'connected' ? 'Connected' : 'Disconnected';
      setStatus(newStatus);
      setLastChecked(new Date());
      return newStatus;
    } catch (error) {
      console.error('Error checking broker connection:', error);
      const errorStatus = 'Error';
      setStatus(errorStatus);
      setLastChecked(new Date());
      return errorStatus;
    } finally {
      setIsChecking(false);
    }
  }, [userAccount, supportsBrokerAPI]);

  // Manual refresh function
  const refresh = useCallback(() => {
    return checkConnection();
  }, [checkConnection]);

  // Effect to check connection on mount and set up interval
  useEffect(() => {
    if (!userAccount) return;

    // Initial check
    checkConnection();

    // Set up interval only for accounts that support broker API
    if (supportsBrokerAPI() && refreshInterval > 0) {
      const interval = setInterval(checkConnection, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [userAccount, checkConnection, supportsBrokerAPI, refreshInterval]);

  return {
    status,
    isChecking,
    lastChecked,
    refresh,
    supportsBrokerAPI: supportsBrokerAPI()
  };
};

/**
 * Get status colors for UI display
 * @param {string} status - Connection status
 * @returns {string} CSS classes for status display
 */
export const getConnectionStatusColors = (status) => {
  const statusColors = {
    'Connected': 'bg-green-500/10 text-green-400',
    'Disconnected': 'bg-red-500/10 text-red-400',
    'Unknown': 'bg-yellow-500/10 text-yellow-400',
    'Error': 'bg-red-500/10 text-red-400',
    'Timeout': 'bg-yellow-500/10 text-yellow-400',
    'Checking...': 'bg-blue-500/10 text-blue-400',
    'N/A': 'bg-gray-500/10 text-gray-400'
  };
  
  return statusColors[status] || 'bg-gray-500/10 text-gray-400';
};

// Export both the named function and as default
export const useBrokerConnection = useIBGatewayConnection;

export default useIBGatewayConnection;